import { useCallback, type FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { Button } from '@trello/nachos/button';
import { Popover, PopoverPlacement, usePopover } from '@trello/nachos/popover';
import { token } from '@trello/theme';

import type { SmartScheduleOnboardingAiRuleType } from '../useUpsertSmartScheduleOnboardingAiRule';

import * as styles from './SmartScheduleRuleTypeInfoButton.module.less';

interface SmartScheduleRuleTypeInfoButtonProps {
  ruleType: SmartScheduleOnboardingAiRuleType;
  onInfoClick: (ruleType: SmartScheduleOnboardingAiRuleType) => void;
}

export const SmartScheduleRuleTypeInfoButton: FunctionComponent<
  SmartScheduleRuleTypeInfoButtonProps
> = ({ ruleType, onInfoClick }) => {
  const intl = useIntl();
  const { triggerRef, toggle, popoverProps } = usePopover({
    placement: PopoverPlacement.BOTTOM,
  });

  const isSelectionRule = ruleType === 'PLANNER_SELECTION';

  const handleClick = useCallback(() => {
    // Only treat opening the popover as engaging with the info button; the same
    // click while open just dismisses it.
    if (!popoverProps.isVisible) {
      onInfoClick(ruleType);
    }
    toggle();
  }, [onInfoClick, popoverProps.isVisible, ruleType, toggle]);

  const popoverTitle = isSelectionRule
    ? intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.card-selection-title',
        defaultMessage: 'Card selection',
        description: 'Popover title for card selection rules',
      })
    : intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.scheduling-rules-title',
        defaultMessage: 'Scheduling rules',
        description: 'Popover title for scheduling preference rules',
      });

  const popoverDescription = isSelectionRule
    ? intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.card-selection-description',
        defaultMessage:
          'Specify general rules for which cards you would like to smart schedule.',
        description: 'Popover description for card selection rules',
      })
    : intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.scheduling-rules-description',
        defaultMessage:
          'Specify general rules for when you would like selected cards scheduled.',
        description: 'Popover description for scheduling preference rules',
      });

  const popoverExample = isSelectionRule
    ? intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.card-selection-example',
        defaultMessage:
          "e.g. Cards due within 5 days; Exclude cards labeled 'Errands'...",
        description: 'Popover example for card selection rules',
      })
    : intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.scheduling-rules-example',
        defaultMessage: 'e.g. Code related tasks in the morning',
        description: 'Popover example for scheduling preference rules',
      });

  const ariaLabel = isSelectionRule
    ? intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.card-selection-aria-label',
        defaultMessage: 'Information about card selection rules',
        description: 'Accessible label for the card selection info button',
      })
    : intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.scheduling-rules-aria-label',
        defaultMessage: 'Information about scheduling preference rules',
        description:
          'Accessible label for the scheduling preference info button',
      });

  return (
    <>
      <Button
        appearance="subtle"
        iconBefore={
          <InformationCircleIcon
            label=""
            color={
              popoverProps.isVisible
                ? token('color.icon.inverse', '#FFFFFF')
                : token('color.icon.disabled', '#091E424F')
            }
          />
        }
        ref={triggerRef}
        onClick={handleClick}
        isSelected={popoverProps.isVisible}
        aria-label={ariaLabel}
      />
      <Popover {...popoverProps} title={popoverTitle}>
        <div className={styles.popoverContent}>
          <span>{popoverDescription}</span>
          <span>{popoverExample}</span>
        </div>
      </Popover>
    </>
  );
};
